import clsx from "clsx";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, blueMui, greyMui, redMui, rydeColors } from "@ryde/theme";

import { useDecorateWithMark } from "../../utils";
import { IRenderColumnTemplate } from "./_core";

const NumberCellTemplateWrapper = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "number-editable-cell-wrapper",
})`
  color: ${blueGreyMui._500};
  display: flex;
  align-items: center;
`;

const NumberCellInputStyled = styled.input.attrs<IStyledAttributes>({
  "data-e2e": "number-editable-cell",
})`
  font-family: inherit;
  font-size: 14px;
  border-radius: 2px;
  width: 45px;
  text-align: center;
  padding: 3px;
  border: 1px solid ${blueMui._300};
  background-color: ${rydeColors.blueTimeEditBg};
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  &:focus {
    color: ${rydeColors.greyFocusedInput};
    background-color: ${rydeColors.white};
    border-color: ${blueMui._300};
    outline: 0;
    box-shadow: 0 0 0 0.2rem ${rydeColors.blueTimeEditBg};
  }
  &:disabled,
  &:read-only {
    background-color: ${greyMui.A100};
    opacity: 1;
  }
  &.error {
    background-color: ${redMui.A400};
    border-color: ${redMui.A400};
    color: ${rydeColors.white};
  }
`;

export const NumberCellTemplate: FC<IRenderColumnTemplate> = ({
  value: initialValue,
  gridAttributes,
  editFn,
  rowId,
}) => {
  const { decorateValue } = useDecorateWithMark();
  const [hasError, setHasError] = useState(false);
  const refValue = useRef(initialValue);
  const [value, setValue] = useState(
    initialValue === null ? undefined : initialValue,
  );

  const displayValue = useMemo(() => {
    return decorateValue(String(value));
  }, [value, decorateValue]);

  useEffect(() => {
    if (initialValue !== refValue.current) {
      setValue(initialValue);
      refValue.current = initialValue;
    }
  }, [initialValue]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(e.target.value);

    if (newValue < 0) {
      setHasError(true);
      return;
    }

    setHasError(false);
    setValue(newValue);
  };

  const handleUpdate = (): void => {
    if (value && rowId && editFn) {
      editFn(rowId, { seats: value });
    }
  };

  return gridAttributes?.isEditable ? (
    <NumberCellTemplateWrapper>
      <NumberCellInputStyled
        onChange={handleChange}
        onBlur={handleUpdate}
        className={clsx({ error: hasError })}
        type="number"
        value={value}
      />
    </NumberCellTemplateWrapper>
  ) : (
    <NumberCellTemplateWrapper data-e2e-template="number-cell">
      {displayValue}
    </NumberCellTemplateWrapper>
  );
};
